import {computed, inject, Injectable, signal} from '@angular/core';
import {StorageService} from '../storage/storage.service';
import { jwtDecode } from 'jwt-decode';

const AUTH_TOKEN_KEY = 'token';

interface DecodedAuthToken {
  expires_on: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly storageService = inject(StorageService);
  private authToken = signal<string | null>(null);
  private logoutTimerId: ReturnType<typeof setTimeout> | null = null;

  isAuthenticated = computed(() => this.authToken() !== null);
  // isAuthenticated = computed(() => true);

  constructor() {
    this.initializeAuthStateAndTimer()
  }

  login(token: string): void {
    this.scheduleLogoutTimer(token);
    this.storageService.setItem(AUTH_TOKEN_KEY, token);
    this.authToken.set(token);
  }

  logout(): void {
    this.clearLogoutTimer();
    this.storageService.removeItem(AUTH_TOKEN_KEY);
    this.authToken.set(null);
  }

  getAuthToken(): string | null {
    return this.authToken();
  }

  private initializeAuthStateAndTimer(): void {
    const token = this.storageService.getItem(AUTH_TOKEN_KEY);
    if (token && !this.isTokenExpired(token)) {
      this.authToken.set(token);
      this.scheduleLogoutTimer(token);
    } else {
      if (token) {
        this.storageService.removeItem(AUTH_TOKEN_KEY);
      }
      this.authToken.set(null);
    }
  }

  private isTokenExpired(token: string): boolean {
    try {
      const decodedToken = jwtDecode<DecodedAuthToken>(token);
      return new Date(decodedToken.expires_on).valueOf() < new Date().valueOf();
    } catch {
      return true;
    }
  }

  private clearLogoutTimer(): void {
    if (this.logoutTimerId) {
      clearTimeout(this.logoutTimerId);
      this.logoutTimerId = null;
    }
  }

  private scheduleLogoutTimer(token: string): void {
    this.clearLogoutTimer();

    try {
      const decodedToken = jwtDecode<DecodedAuthToken>(token);
      const expiresInMs = new Date(decodedToken.expires_on).getTime() - new Date().getTime();
      // const expiresInMs = 5000;

      if (expiresInMs > 0) {
        this.logoutTimerId = setTimeout(() => {
          this.logout();
        }, expiresInMs);
      } else {
        this.logout();
      }
    } catch (error) {
      this.logout();
    }
  }
}
